import { useEffect, useId, useRef, useState } from 'react'
import { Icon } from '../Icon'
import { JourneyCalendar } from './JourneyCalendar'
import type { JourneyCalendarLoader, JourneyCalendarDay } from '../../data/DataSource'
import { scheduleToday } from '../../domain/scheduling'

export function JourneyDateInput({ label, value, min, max, days, onChange, onOpen, loadCalendar }: {
  loadCalendar?: JourneyCalendarLoader
  label: string; value: string; min?: string; max?: string
  days: { date: string; hasCompleteJourney: boolean; journeyCount: number }[]
  onChange: (date: string) => void; onOpen?: () => void
}) {
  const [open, setOpen] = useState(false)
  const [month, setMonth] = useState(() => (value || scheduleToday()).slice(0, 7))
  const [remoteDays, setRemoteDays] = useState<JourneyCalendarDay[]>([])
  const [state, setState] = useState<'loading' | 'error' | 'ready'>('loading')
  const [retry, setRetry] = useState(0)
  useEffect(() => {
    if (!open || !loadCalendar) {return}
    let alive = true
    const controller = new AbortController()
    setState('loading')
    setRemoteDays([])
    void loadCalendar(month, controller.signal).then((items) => {
      if (alive) {setRemoteDays(items); setState('ready')}
    }).catch(() => {if (alive) {setState('error')}})
    return () => {alive = false; controller.abort()}
  }, [open, month, loadCalendar, retry])
  const id = useId()
  const rootRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const calendarRef = useRef<HTMLDivElement>(null)
  const close = () => {setOpen(false); buttonRef.current?.focus()}
  useEffect(() => {
    if (!open) {return}
    calendarRef.current?.focus()
    const outside = (event: Event) => {
      if (!rootRef.current?.contains(event.target as Node)) {setOpen(false)}
    }
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {event.preventDefault(); setOpen(false); buttonRef.current?.focus()}
    }
    document.addEventListener('pointerdown', outside)
    document.addEventListener('focusin', outside)
    document.addEventListener('keydown', escape)
    return () => {
      document.removeEventListener('pointerdown', outside)
      document.removeEventListener('focusin', outside)
      document.removeEventListener('keydown', escape)
    }
  }, [open])
  const formatted = value ? value.split('-').reverse().join('/') : 'Selecionar data'
  return <div className="fz-listfilters__datefield" ref={rootRef}>
    <span id={id}>{label}</span>
    <button ref={buttonRef} type="button" className="fz-listfilters__datebutton" aria-label={label} aria-haspopup="dialog" aria-controls={`${id}-calendar`} aria-expanded={open} onClick={() => {
      if (!open) {setMonth((value || min || max || scheduleToday()).slice(0, 7)); onOpen?.()}
      setOpen(!open)
    }}>{formatted}<Icon name="calendar" size={14} /></button>
    {open && <div ref={calendarRef} id={`${id}-calendar`} className="fz-listfilters__datepopover" role="dialog" aria-labelledby={`${id}-dialog`} tabIndex={-1}>
        <div className="fz-listfilters__datehead"><h2 id={`${id}-dialog`}>{label}</h2><button type="button" className="fz-iconbtn" aria-label="Fechar calendário" onClick={close}><Icon name="xPlain" size={16} /></button></div>
        <JourneyCalendar month={month} selectedDate={value || null} days={loadCalendar ? remoteDays.map((day) => ({ ...day, hasCompleteJourney: true })) : days} mode="date-filter" minDate={min} maxDate={max}
          state={loadCalendar ? state : 'ready'} onRetry={() => setRetry((n) => n + 1)} onMonthChange={setMonth} onSelect={(date) => {onChange(date); close()}} />
        {value && <button type="button" className="fz-btn fz-btn--ghost fz-btn--sm" onClick={() => {onChange(''); close()}}>Limpar data</button>}
    </div>}
  </div>
}
